import { useState } from 'react';
import { C } from '@ridgewill/brand';
import { adminApi, type AdminUser, type ApiKey } from '../lib/api.js';
import { useAsync, useAction } from '../lib/useAsync.js';
import { useChips } from '../lib/useChips.js';
import { breakGlassRole, splitRoles } from '../lib/roles.js';
import { count } from '../lib/format.js';
import {
  ChipBar,
  FilterPills,
  InfoBubble,
  Menu,
  MenuItem,
  PrimaryButton,
  Screen,
  inputStyle,
} from '../components/Screen.js';
import { useCan, whyDisabled } from '../lib/capabilities.js';
import {
  AsyncBoundary,
  Avatar,
  Badge,
  Button,
  DotPill,
  EmptyState,
  Field,
  HoldButton,
  Note,
  OutcomeNote,
  Panel,
  PanelBody,
  Table,
  Td,
  When,
} from '../components/ui.js';
import { Modal, ModalActions } from '../components/Modal.js';
import { RecordScreen, resolveTab, type RecordTabs } from '../components/Record.js';
import { AddAgent } from '../modals/AddAgent.js';
import { IssueCredential } from '../modals/IssueCredential.js';
import { RotateCredential } from '../modals/RotateCredential.js';
import { EditCredential, canEditCredential } from '../modals/EditCredential.js';
import { UserAccessTab } from './UserAccess.js';
import { Pager, RoleBadges, PAGE } from './Users.js';
import { agentOf, roleOf } from './Developer.js';

/**
 * Automated agents: their own list and their own record (Jon, 2026-09-22).
 *
 * ── THIS WAS A MODE OF THE USERS SCREEN, AND JON ASKED FOR ITS OWN ────
 *
 * The first version was one list in two modes, on the argument that an agent
 * account is a user account and two copies drift. Jon asked for a separate
 * list, and the questions really are different: of a person you ask whether
 * they can sign in, of an agent you ask what keys it holds and when they were
 * last used. So this screen shares the small pieces (the pager, the role
 * badges, the Access tab) and owns the rest.
 *
 * ── WHAT THE RECORD LEAVES OUT ──────────────────────────────────────
 *
 * Sessions, passkeys, two-factor, agreements and the ban form. An agent cannot
 * sign in (auth#539), so each of those is a panel that is always empty, and an
 * always-empty panel reads as a state the platform might put an agent in.
 */
export function Agents({ id, tab }: { id?: string; tab?: string }) {
  return id ? <AgentRecord key={id} id={id} tab={tab} /> : <AgentList />;
}

// ── Pure helpers, exported for the tests ────────────────────────────

/**
 * This agent's keys, and only this agent's.
 *
 * The credential search matches its terms against the key's name, prefix and
 * start as well as its owner, so searching by an id can in principle return a
 * key that merely mentions it. The owner check is what makes the list true.
 */
export function keysOwnedBy(keys: ApiKey[], ownerId: string): ApiKey[] {
  return keys.filter((k) => k.ownerId === ownerId);
}

/** A key is live unless it is revoked or past its expiry. */
export function isLive(k: ApiKey, now: number = Date.now()): boolean {
  if (k.enabled === false) return false;
  if (k.expiresAt && Date.parse(k.expiresAt) <= now) return false;
  return true;
}

/**
 * Whether a key acts with a role the account no longer holds.
 *
 * A key's role is fixed when it is issued and rotation keeps it, while the
 * account's role is edited on the Access tab. The two can drift apart, and a
 * key still carrying `global-admin` after its agent was moved down is exactly
 * the thing somebody auditing this screen needs to see.
 */
export function keyRoleDrift(keyRole: string | null, accountRole: unknown): boolean {
  if (!keyRole) return false;
  return !splitRoles(accountRole).includes(keyRole);
}

type AgentStatus = 'all' | 'active' | 'revoked';

export function agentStatus(u: AdminUser): 'active' | 'revoked' | 'closed' {
  if (u.pseudonymized) return 'closed';
  if (u.banned) return 'revoked';
  return 'active';
}

function AgentStatusPill({ user }: { user: AdminUser }) {
  const s = agentStatus(user);
  if (s === 'closed') return <DotPill tone="muted">closed</DotPill>;
  if (s === 'revoked') return <DotPill tone="danger">revoked</DotPill>;
  return <DotPill tone="good">active</DotPill>;
}

/**
 * Who made the agent (RidgeWill/auth#544).
 *
 * `undefined` is an auth that predates the field and `null` is an agent with
 * nobody on record; they are said differently because only one of them is a
 * fact about the agent.
 */
export function sponsorLabel(sponsor: AdminUser['sponsor']): string {
  if (sponsor === undefined) return 'unknown';
  if (sponsor === null) return 'none on record';
  return sponsor.name || sponsor.email || 'closed account';
}

function Sponsor({ sponsor }: { sponsor: AdminUser['sponsor'] }) {
  if (!sponsor) return <span style={{ color: C.muted }}>{sponsorLabel(sponsor)}</span>;
  return (
    <a href={`#/users/${encodeURIComponent(sponsor.id)}`} style={{ color: C.brand, textDecoration: 'none' }}>
      {sponsorLabel(sponsor)}
    </a>
  );
}

// ── The list ─────────────────────────────────────────────────────────

function AgentList() {
  const [status, setStatus] = useState<AgentStatus>('all');
  const [offset, setOffset] = useState(0);
  const [adding, setAdding] = useState(false);
  const mayAdd = useCan('user:create');

  const search = useChips([], () => setOffset(0));

  const rolesMeta = useAsync(() => adminApi.roles());
  const known =
    rolesMeta.state.status === 'ready'
      ? {
          labels: rolesMeta.state.data.labels,
          breakGlass: breakGlassRole(rolesMeta.state.data),
        }
      : {};

  const { state, reload } = useAsync(
    () => adminApi.users({ terms: search.active, limit: PAGE, offset, agents: 'only' }),
    [search.key, offset],
  );


  return (
    <Screen
      filters={
        <FilterPills
          label="Filter by status"
          current={status}
          onSelect={setStatus}
          options={[
            { value: 'all', label: 'All' },
            { value: 'active', label: 'Active' },
            { value: 'revoked', label: 'Revoked' },
          ]}
        />
      }
      actions={
        <PrimaryButton
          disabled={!mayAdd}
          title={mayAdd ? undefined : whyDisabled('creating agents')}
          onClick={() => setAdding(true)}
        >
          + Add agent
        </PrimaryButton>
      }
      chips={
        <ChipBar
          search={search}
          placeholder="Search name, identifier, id or sponsor, then press Enter…"
          appliedByServer={state.status === 'ready' ? state.data.terms : undefined}
        />
      }
    >
      <Panel title="Agents">
        <AsyncBoundary state={state}>
          {(d) => {
            const rows = d.users.filter((u) => status === 'all' || agentStatus(u) === status);
            return (
              <>
                {rows.length === 0 ? (
                  <EmptyState
                    title={
                      search.active.length > 0
                        ? 'No agents match your search'
                        : status !== 'all'
                          ? `No ${status} agents on this page`
                          : 'No agents yet'
                    }
                    suggestion={
                      search.active.length > 0
                        ? 'Try a different name, identifier, id or sponsor.'
                        : status === 'all'
                          ? 'Add one above. It gets an account, a role and, if you want, a credential.'
                          : undefined
                    }
                  />
                ) : (
                  <Table columns={['Agent', 'Role', 'Last activity', 'Status', 'Created', 'Sponsor', '']}>
                    {rows.map((u) => {
                      const href = `#/agents/${encodeURIComponent(u.id)}`;
                      return (
                        <tr
                          key={u.id}
                          className="rw-rowhover"
                          style={{ opacity: agentStatus(u) === 'active' ? 1 : 0.55 }}
                        >
                          <Td>
                            <a href={href} style={rowLink}>
                              {u.name || u.email || u.id}
                            </a>
                            {u.name && u.email ? <div style={sub}>{u.email}</div> : null}
                          </Td>
                          <Td>
                            <RoleBadges role={u.role} labels={known.labels} breakGlass={known.breakGlass} />
                          </Td>
                          <Td>
                            {/* The latest use of any key: the only way an agent acts. */}
                            {u.lastActivityAt ? (
                              <When iso={u.lastActivityAt} />
                            ) : (
                              <span style={{ color: C.muted }}>
                                {u.lastActivityAt === null ? 'never' : 'unknown'}
                              </span>
                            )}
                          </Td>
                          <Td>
                            <AgentStatusPill user={u} />
                          </Td>
                          <Td>
                            <When iso={u.createdAt} />
                          </Td>
                          <Td>
                            <Sponsor sponsor={u.sponsor} />
                          </Td>
                          <Td>
                            <Menu label={`Actions for ${u.name || u.email || u.id}`}>
                              {(close) => (
                                <>
                                  <MenuItem href={href} onClick={close}>
                                    Open
                                  </MenuItem>
                                  <MenuItem href={`${href}/credentials`} onClick={close}>
                                    Credentials
                                  </MenuItem>
                                  <MenuItem
                                    href={`#/activity?user=${encodeURIComponent(u.id)}`}
                                    onClick={close}
                                  >
                                    View activity
                                  </MenuItem>
                                </>
                              )}
                            </Menu>
                          </Td>
                        </tr>
                      );
                    })}
                  </Table>
                )}

                <Pager
                  shown={rows.length}
                  page={d.users.length}
                  total={d.total}
                  offset={offset}
                  onPage={setOffset}
                  filtered={status !== 'all'}
                />

              </>
            );
          }}
        </AsyncBoundary>
      </Panel>

      {adding ? (
        <AddAgentDialog
          onClose={() => setAdding(false)}
          reload={reload}
          onCreated={() => {
            setAdding(false);
            reload();
          }}
        />
      ) : null}
    </Screen>
  );
}

/** Fetches the application list only while the dialog is open. */
function AddAgentDialog(props: { onClose: () => void; onCreated: () => void; reload: () => void }) {
  const apps = useAsync(() => adminApi.applications(), []);
  const applications =
    apps.state.status === 'ready' ? apps.state.data.applications.filter((a) => a.active) : [];
  return <AddAgent applications={applications} {...props} />;
}

// ── The record ───────────────────────────────────────────────────────

const TABS = [
  { path: 'overview', label: 'Overview' },
  { path: 'credentials', label: 'Credentials' },
  { path: 'access', label: 'Access' },
] as const satisfies RecordTabs;

function AgentRecord({ id, tab }: { id: string; tab?: string }) {
  const { state, reload } = useAsync(() => adminApi.user(id), [id]);
  const current = resolveTab(TABS, tab);

  return (
    <AsyncBoundary state={state}>
      {(user) => (
        <RecordScreen
          back={{ to: '/agents', label: 'Agents' }}
          avatar={<Avatar id={user.id} label={user.name || user.email || user.id} image={user.image} />}
          /* The NAME is the heading here, not the address. For a person the
             email is what staff say out loud; for an agent the address is only
             an identifier, and "Claude support agent" is what it is called. */
          title={user.name || user.email || 'Closed account'}
          badges={<AgentStatusPill user={user} />}
          description={user.name && user.email ? user.email : undefined}
          actions={<Button href={`#/activity?user=${encodeURIComponent(user.id)}`}>View activity</Button>}
          tabs={TABS}
          base={`/agents/${encodeURIComponent(user.id)}`}
          current={current}
        >
          {user.isAgent !== true ? (
            /* Reachable by typing an address. Saying so beats rendering a
               person's account on a screen that will not show their sessions. */
            <Note tone="warn">
              This account is not an agent. <a href={`#/users/${encodeURIComponent(user.id)}`}>Open it
              under Users</a>.
            </Note>
          ) : null}
          {current === 'overview' ? <Overview user={user} onChanged={reload} /> : null}
          {current === 'credentials' ? <Credentials user={user} /> : null}
          {current === 'access' ? <UserAccessTab user={user} onChanged={reload} /> : null}
        </RecordScreen>
      )}
    </AsyncBoundary>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: '.2rem' }}>
      <span style={factLabel}>{label}</span>
      <span style={{ fontSize: '.88rem' }}>{children}</span>
    </div>
  );
}

function Overview({ user, onChanged }: { user: AdminUser; onChanged: () => void }) {
  const rolesMeta = useAsync(() => adminApi.roles());
  const labels = rolesMeta.state.status === 'ready' ? rolesMeta.state.data.labels : undefined;
  const breakGlass =
    rolesMeta.state.status === 'ready'
      ? breakGlassRole(rolesMeta.state.data)
      : undefined;

  return (
    <>
      <Panel
        title="Identity"
        info={
          <InfoBubble label="About this agent's identity">
            The <strong>identifier</strong> is an address that names this agent. Nobody signs in with
            it and it cannot reset a password.
            <br />
            <br />
            The <strong>role</strong> is what the agent may do, exactly as for a person. Change it on
            the Access tab. Each key has its own role too, so after a change, edit the agent&rsquo;s
            keys on the Credentials tab to match.
            <br />
            <br />
            The <strong>sponsor</strong> is the person who created or designated the agent.
          </InfoBubble>
        }
      >
        <PanelBody>
          <div style={facts}>
            <Fact label="Name">{user.name ?? <span style={{ color: C.muted }}>none set</span>}</Fact>
            <Fact label="Identifier">
              {user.email ? <code style={mono}>{user.email}</code> : <span style={{ color: C.muted }}>none</span>}
            </Fact>
            <Fact label="Role">
              <RoleBadges role={user.role} labels={labels} breakGlass={breakGlass} />
            </Fact>
            <Fact label="Created">
              <When iso={user.createdAt} />
            </Fact>
            <Fact label="Sponsor">
              <Sponsor sponsor={user.sponsor} />
            </Fact>
          </div>
        </PanelBody>
      </Panel>

      <Panel
        title={user.banned ? 'Revoked' : 'Revoke'}
        info={
          <InfoBubble label="What revoking does">
            Revoking stops the agent acting: the account is banned and every live key it holds is
            revoked at once. The record and its activity stay.
            <br />
            <br />
            Reinstating lifts the ban only. Revoked keys stay revoked, so a reinstated agent needs a
            new key before it can act.
          </InfoBubble>
        }
      >
        <PanelBody>
          <Revocation user={user} onChanged={onChanged} />
        </PanelBody>
      </Panel>
    </>
  );
}

/**
 * Stopping the agent, and letting it act again.
 *
 * ── A BAN ALONE DOES NOT STOP AN AGENT ──────────────────────────────
 *
 * Banning revokes sessions and refuses sign-in, and an agent has neither: it
 * acts with API keys, and the key check does not look at the account's ban.
 * So revoking here is the ban AND every live key, one after another, with the
 * outcome saying how many keys were revoked. A button labelled "Revoke" that
 * left the keys working would be worse than no button, because it would be
 * the thing somebody pressed during an incident and then stopped worrying.
 *
 * Reinstating lifts the ban only. Revoked keys stay revoked (the service does
 * not un-revoke), so an agent brought back needs a new key, and the outcome
 * says so rather than leaving it to be discovered when the agent fails.
 */
function Revocation({ user, onChanged }: { user: AdminUser; onChanged: () => void }) {
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const action = useAction();
  const mayRevoke = useCan('credential:revoke');

  if (user.banned) {
    return (
      <div style={{ display: 'grid', gap: '.7rem' }}>
        <OutcomeNote outcome={action.outcome} />
        <p style={para}>
          This agent is revoked{user.banReason ? `: ${user.banReason}` : '.'} Its keys were revoked with
          it.
        </p>
        <div>
          <Button
            disabled={action.running}
            onClick={() =>
              void action
                .run(async () => {
                  await adminApi.unbanUser(user.id);
                  return 'Reinstated. Its old keys stay revoked, so issue it a new one before it can act.';
                })
                .then(onChanged)
            }
          >
            Reinstate
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: '.7rem' }}>
      <OutcomeNote outcome={action.outcome} />
      <div>
        <Button
          tone="danger"
          disabled={action.running || !mayRevoke}
          title={mayRevoke ? undefined : whyDisabled('revoking credentials')}
          onClick={() => setConfirming(true)}
        >
          Revoke agent
        </Button>
      </div>

      {confirming ? (
        <Modal
          title={`Revoke ${user.name || user.email || 'this agent'}`}
          onClose={() => setConfirming(false)}
          width={460}
          footer={
            <ModalActions>
              <Button onClick={() => setConfirming(false)}>Cancel</Button>
              <HoldButton
                title="Hold for a second and a half to revoke this agent and every live key it holds."
                disabled={action.running || !reason.trim()}
                onHold={() =>
                  void action
                    .run(async () => {
                      /* The ban FIRST, so an agent mid-way through a failed
                         revocation is at least marked revoked on every screen. */
                      await adminApi.banUser(user.id, reason.trim());
                      const all = await adminApi.credentials({ terms: [user.id], limit: 100 });
                      const live = keysOwnedBy(all.apiKeys.keys, user.id).filter((k) => k.enabled !== false);
                      let done = 0;
                      for (const k of live) {
                        await adminApi.revokeApiKey(k.id);
                        done++;
                      }
                      return `Revoked. ${count(done)} key${done === 1 ? '' : 's'} revoked with it.`;
                    })
                    .then(() => {
                      setConfirming(false);
                      onChanged();
                    })
                }
              >
                {action.running ? 'Revoking…' : 'Revoke agent'}
              </HoldButton>
            </ModalActions>
          }
        >
          <p style={para}>
            Every live key this agent holds stops working now. Reinstating it later does not bring
            them back.
          </p>
          <Field label="Reason">
            <input value={reason} onChange={(e) => setReason(e.target.value)} style={inputStyle} />
          </Field>
        </Modal>
      ) : null}
    </div>
  );
}

/**
 * This agent's keys, with rotate and revoke.
 *
 * Found by owner id (RidgeWill/auth#542), then filtered on the owner exactly,
 * for the reason `keysOwnedBy` gives.
 */
function Credentials({ user }: { user: AdminUser }) {
  const { state, reload } = useAsync(
    () => adminApi.credentials({ terms: [user.id], limit: 100 }),
    [user.id],
  );
  const action = useAction();
  const mayRevoke = useCan('credential:revoke');
  const [rotating, setRotating] = useState<ApiKey | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [editing, setEditing] = useState<ApiKey | null>(null);
  const revoked = agentStatus(user) !== 'active';

  return (
    <Panel
      title="Credentials"
      info={
        <InfoBubble label="About this agent's keys">
          <strong>Edit</strong> changes a key&rsquo;s name, capabilities, role or expiry. It keeps
          the same secret.
          <br />
          <br />
          <strong>Rotate</strong> gives the same key a new secret. Its permissions, role and expiry
          stay, and the old secret stops working at once, so update wherever the agent reads it from
          before its next run.
          <br />
          <br />
          <strong>Revoke</strong> disables the key. It stops working immediately and the row stays so
          what it did can still be answered.
          <br />
          <br />
          A key has its own role, set when it is issued. A key marked{' '}
          <em>role differs from account</em> acts with a role this agent no longer holds. Edit the
          key to change it.
        </InfoBubble>
      }
      action={
        /* `credential:revoke` is the issuing permission too: the service has
           no separate `issue` action, because the two are the same authority. */
        <Button
          disabled={!mayRevoke || revoked}
          title={
            !mayRevoke
              ? whyDisabled('issuing credentials')
              : revoked
                ? 'Reinstate the agent before issuing it a key.'
                : undefined
          }
          onClick={() => setIssuing(true)}
        >
          Issue key
        </Button>
      }
    >
      <div style={{ padding: '1rem 1.2rem 0' }}>
        <OutcomeNote outcome={action.outcome} />
      </div>
      <AsyncBoundary state={state}>
        {(d) => {
          const keys = keysOwnedBy(d.apiKeys.keys, user.id);
          if (keys.length === 0) {
            return (
              <EmptyState
                title="This agent holds no keys"
                suggestion="A key is issued when the agent is added. Without one it cannot act."
              />
            );
          }
          return (
            <Table columns={['Key', 'Service', 'Last used', 'Expires', 'Status', '']}>
              {keys.map((k) => {
                const keyRole = roleOf(k.metadata);
                const drift = keyRoleDrift(keyRole, user.role);
                const live = isLive(k);
                return (
                  <tr key={k.id}>
                    <Td>
                      <strong>{k.name ?? 'unnamed'}</strong>
                      <div style={{ color: C.muted, fontSize: '.8rem', fontFamily: 'ui-monospace, monospace' }}>
                        {k.start ?? k.prefix ?? 'no prefix'}…
                      </div>
                    </Td>
                    <Td mono>
                      {typeof k.metadata?.appSlug === 'string' ? k.metadata.appSlug : 'no app'}
                      <div style={{ display: 'flex', gap: '.3rem', flexWrap: 'wrap', marginTop: '.3rem' }}>
                        {!agentOf(k.metadata) ? <Badge tone="danger">not marked agent</Badge> : null}
                        {keyRole ? <Badge tone="warn">{keyRole}</Badge> : null}
                        {drift ? <Badge tone="danger">role differs from account</Badge> : null}
                        {k.grants?.map((g) => <Badge key={g}>{g}</Badge>)}
                      </div>
                    </Td>
                    <Td>
                      <When iso={k.lastRequest} />
                      {k.requestCount ? (
                        <div style={{ color: C.muted, fontSize: '.8rem' }}>{count(k.requestCount)} requests</div>
                      ) : null}
                    </Td>
                    <Td>
                      {k.expiresAt ? <When iso={k.expiresAt} /> : <span style={{ color: C.muted }}>never</span>}
                    </Td>
                    <Td>
                      {k.enabled === false ? (
                        <Badge tone="danger">revoked</Badge>
                      ) : live ? (
                        <Badge tone="good">active</Badge>
                      ) : (
                        <Badge tone="warn">expired</Badge>
                      )}
                    </Td>
                    <Td>
                      {k.enabled === false ? null : (
                        <span style={{ display: 'inline-flex', gap: '.4rem' }}>
                          {canEditCredential(k) && (
                            <Button
                              disabled={action.running || !mayRevoke}
                              title={mayRevoke ? undefined : whyDisabled('editing credentials')}
                              onClick={() => setEditing(k)}
                            >
                              Edit
                            </Button>
                          )}
                          <Button
                            disabled={action.running || !mayRevoke}
                            title={mayRevoke ? undefined : whyDisabled('rotating credentials')}
                            onClick={() => setRotating(k)}
                          >
                            Rotate
                          </Button>
                          <HoldButton
                            disabled={action.running || !mayRevoke}
                            title={mayRevoke ? `Hold for a second and a half to revoke ${k.name ?? 'this key'}. It stops working at once.` : whyDisabled('revoking credentials')}
                            onHold={() =>
                              void action
                                .run(async () => {
                                  const r = await adminApi.revokeApiKey(k.id);
                                  return r.alreadyRevoked
                                    ? 'Already revoked. Nothing changed.'
                                    : `Revoked ${k.name ?? 'the key'}. It stops working immediately.`;
                                })
                                .then(() => reload())
                            }
                          >
                            Revoke
                          </HoldButton>
                        </span>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </Table>
          );
        }}
      </AsyncBoundary>

      {issuing ? (
        <IssueKeyDialog
          user={user}
          onClose={() => setIssuing(false)}
          onIssued={reload}
        />
      ) : null}

      {editing ? (
        <EditCredential
          apiKey={editing}
          agentRoles={splitRoles(user.role)}
          onClose={() => setEditing(null)}
          onSaved={reload}
        />
      ) : null}

      {rotating ? (
        <RotateCredential
          apiKey={rotating}
          onClose={() => {
            setRotating(null);
            reload();
          }}
        />
      ) : null}
    </Panel>
  );
}

/** Fetches the application list only while the dialog is open. */
function IssueKeyDialog({
  user,
  onClose,
  onIssued,
}: {
  user: AdminUser;
  onClose: () => void;
  onIssued: () => void;
}) {
  const apps = useAsync(() => adminApi.applications(), []);
  const applications =
    apps.state.status === 'ready' ? apps.state.data.applications.filter((a) => a.active) : [];
  return (
    <IssueCredential
      applications={applications}
      onClose={onClose}
      onIssued={onIssued}
      forAgent={{ id: user.id, name: user.name || user.email || 'this agent', role: user.role }}
    />
  );
}

const rowLink = { fontWeight: 600, color: C.brand, textDecoration: 'none' };
const sub = { fontSize: '.78rem', color: C.muted, marginTop: 2 };
const para = { margin: 0, fontSize: '.86rem', color: C.textMid, lineHeight: 1.5 };
const mono = { fontFamily: 'ui-monospace, monospace', fontSize: '.84rem', wordBreak: 'break-all' as const };
/* The same as the person record's, so the two read as one family. */
const facts = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
  gap: '.9rem 1.4rem',
};
const factLabel = {
  fontSize: '.72rem',
  letterSpacing: '.06em',
  textTransform: 'uppercase' as const,
  color: C.muted,
  fontWeight: 640,
};
